import { useMemo } from 'react';
import { Truck, Play, Wifi } from 'lucide-react';
import { useFleetStore } from '@/stores/fleet';
import { useTagIds } from '@/hooks/useMachines';
import { computeMachineStatus, isGpsFresh } from '@/lib/machineStatus';
import { cn } from '@/lib/utils';

interface Props {
  /** Variante compacta usa só ícone + valor (sem label). Útil em header apertado. */
  dense?: boolean;
}

/**
 * Cards compactos no header com métricas em tempo real:
 * - Total · todas as máquinas conhecidas
 * - Em operação · status running
 * - Online · GPS atualizado nos últimos 60 minutos
 */
export function HeaderStats({ dense }: Props) {
  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);
  const tagIds = useTagIds();

  const stats = useMemo(() => {
    let total = 0;
    let running = 0;
    let online = 0;
    for (const device of devices.values()) {
      if (tagIds.has(device.id)) continue; // Tags não contam como máquina
      total += 1;
      const position = positions.get(device.id);
      const status = computeMachineStatus(device, position);
      if (status === 'running') running += 1;
      if (isGpsFresh(device, position)) online += 1;
    }
    return { total, running, online };
  }, [devices, positions, tagIds]);

  return (
    <div className="flex items-center gap-2">
      <Stat icon={Truck} label="Total" value={stats.total} dense={dense} />
      <Stat
        icon={Play}
        label="Em operação"
        value={stats.running}
        accent="text-status-running"
        dense={dense}
      />
      <Stat
        icon={Wifi}
        label="Online"
        value={stats.online}
        accent="text-primary"
        dense={dense}
      />
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  accent,
  dense,
}: {
  icon: typeof Truck;
  label: string;
  value: number;
  accent?: string;
  dense?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5"
      title={label}
    >
      <Icon className={cn('h-4 w-4 text-muted-foreground', accent)} />
      {dense ? (
        <span className="text-sm font-bold">{value.toLocaleString('pt-BR')}</span>
      ) : (
        <div className="leading-tight">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className="text-sm font-bold">{value.toLocaleString('pt-BR')}</p>
        </div>
      )}
    </div>
  );
}
